import {LinkText} from '@/components/atoms';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {useActivites} from '@/containers/Activity/useActivites';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {formatDateToLocalYYYYMMDD} from '@/utility/dateHelper';
import {useCallback, useMemo, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';

const cellDataMapperObj = (navigate) => ({
  activityTitle: {
    cellData: ({row: {original} = {}}) => {
      const queryParam = `?review=${encodeURIComponent(original?.status)}`;
      return (
        <LinkText
          text={original?.activityTitle}
          id={original?.activityTitle + ' ' + original?.id || original?.uuid}
          handleClick={() =>
            navigate(
              `${ROUTES.ACTIVITY_VIEW.replace(':id', encodeURIComponent(original?.id || original?.uuid))}${queryParam}`
            )
          }
        />
      );
    },
    linkHandler: (row) => {
      const queryParam = `?review=${encodeURIComponent(row?.status)}`;
      return navigate(
        `${ROUTES.ACTIVITY_VIEW.replace(':id', encodeURIComponent(row?.id || row?.uuid))}${queryParam}`
      );
    },
    linkLabel: (row) => row.activityTitle || '—'
  },
  customersCount: ({row: {original} = {}}) => {
    return <div>{original?.customersCount ?? '\u2014'}</div>;
  },
  percentCompleted: ({row: {original} = {}}) => {
    return <div>{original?.percentCompleted ?? '\u2014'}</div>;
  },
  pointsAwarded: ({row: {original} = {}}) => {
    return <div>{original?.pointsAwarded ?? '\u2014'}</div>;
  },
  updatedBy: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.updatedBy || '\u2014'}</div>
    )
  },
  status: ({row: {original} = {}}) => {
    return (
      <div className={original?.status === 'Cancelled' ? 'status_error' : ''}>
        {original?.status || '\u2014'}
      </div>
    );
  },
  lastUpdatedDate: {
    cellData: ({row: {original} = {}}) => (
      <div>
        {formatDateToLocalYYYYMMDD(original?.lastUpdatedDate) || '\u2014'}
      </div>
    )
  }
});
export const SalesTargetsActivitiesList = () => {
  const {
    activityListData,
    activityLColConfigData,
    pageIndex,
    setPageIndex,
    pageSize: hookPageSize,
    setPageSize,
    setSortingObj,
    sortingObj,
    setSearchArry,
    onDownloadBtnClick,
    handleResetAll,
    isPending,
    isFetching,
    isError
  } = useActivites();

  const navigate = useNavigate();

  // Map cell renderers onto the backend column config
  const columns = useMemo(
    () =>
      getCellDataMappedCols(
        cellDataMapperObj(navigate),
        activityLColConfigData
      ),
    [navigate, activityLColConfigData]
  );

  const isLoading = isPending;

  const handleDownload = useCallback(
    (rows, {isAllSelected = false, excludeIds = []} = {}) => {
      const ids = isAllSelected
        ? []
        : rows.map((r) => r.original?.id || r.original?.uuid || r.id || r.uuid);
      onDownloadBtnClick(ids, excludeIds);
    },
    [onDownloadBtnClick]
  );

  // Stable records reference — prevents new [] on every render during loading
  const emptyRecords = useRef([]);
  const records = isError
    ? emptyRecords.current
    : (activityListData?.data ?? emptyRecords.current);

  const totalRecords = activityListData?.totalRecords || 0;
  const pageSize = hookPageSize;

  const onRowsPerPageChange = useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPageIndex(0);
    },
    [setPageSize, setPageIndex]
  );

  const paginationConfig = useMemo(
    () => ({pageIndex, setPageIndex, onRowsPerPageChange}),
    [pageIndex, setPageIndex, onRowsPerPageChange]
  );

  const sortingConfig = useMemo(
    () => ({handleSorting: setSortingObj, initialSortConfig: sortingObj}),
    [setSortingObj, sortingObj]
  );

  const filterConfig = useMemo(
    () => ({
      setSearchArry,
      onResetAll: handleResetAll,
      showResetAll: true,
      resetAllPosition: 'left'
    }),
    [setSearchArry, handleResetAll]
  );

  // Header-checkbox select-all spans all pages (legacy UX).
  const selectionConfig = useMemo(() => ({enableSelectAllPages: true}), []);

  const toolbarConfig = useMemo(
    () => ({
      showDownload: true,
      onDownload: handleDownload,
      alwaysShowInToolbar: true
    }),
    [handleDownload]
  );

  return (
    <div className='salesTargetsActiviesDataTable'>
      <PeacockDataTable
        columns={columns}
        records={records}
        isLoading={isLoading}
        isFetching={isFetching}
        pageSize={pageSize}
        totalRecords={totalRecords}
        paginationConfig={paginationConfig}
        selectionConfig={selectionConfig}
        sortingConfig={sortingConfig}
        filterConfig={filterConfig}
        toolbarConfig={toolbarConfig}
        displayConfig={{wrapperClassName: 'sales-target-activities-datatable'}}
      />
      {totalRecords > 0 && (
        <div className='text-md py-2 pr-4 text-right text-[color:var(--color-text-primary)]'>
          {totalRecords} total items
        </div>
      )}
    </div>
  );
};
